import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { IMDFData, FeatureStats } from '../../types/imdf.types';

interface IMDFStatsChartProps {
  imdfData: IMDFData | null;
  expandedFiles?: Set<string>;
  className?: string;
}

const COLORS = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
];

const IMDFStatsChart: React.FC<IMDFStatsChartProps> = ({
  imdfData,
  expandedFiles = new Set(),
  className = '',
}) => {
  // 計算統計數據
  const stats = useMemo(() => {
    if (!imdfData || imdfData.files.length === 0) {
      return {
        categoryStats: [],
        fileStats: [],
        layerStats: [],
        confirmStats: { confirmed: 0, unconfirmed: 0 },
      };
    }

    const categoryCount: { [key: string]: number } = {};
    const fileCount: { [key: string]: number } = {};
    const layerCount: { [key: string]: number } = {};
    let confirmedCount = 0;
    let unconfirmedCount = 0;

    // 取得圖層類型的輔助函數
    const getLayerType = (feature: GeoJSON.Feature): string => {
      const sourceFile = feature.properties?._sourceFile || '';
      
      if (sourceFile.includes('venue')) return 'venue';
      if (sourceFile.includes('building')) return 'building';
      if (sourceFile.includes('level')) return 'level';
      if (sourceFile.includes('unit')) return 'unit';
      if (sourceFile.includes('amenity')) return 'amenity';
      if (sourceFile.includes('opening')) return 'opening';
      if (sourceFile.includes('fixture')) return 'fixture';
      if (sourceFile.includes('anchor')) return 'anchor';
      
      return 'other';
    };

    // 統計各類別、檔案和圖層的 feature 數量
    imdfData.files.forEach((file) => {
      fileCount[file.name] = file.featureCount;

      // 只有展開的檔案才統計分類和其他詳細資訊
      const shouldIncludeFileDetails = expandedFiles.has(file.name);

      file.features.forEach((feature) => {
        // 統計圖層類型（總是統計）
        const layerType = getLayerType(feature);
        layerCount[layerType] = (layerCount[layerType] || 0) + 1;

        // 統計確認狀態（總是統計）
        if (feature.properties?.stoConfirm === true) {
          confirmedCount++;
        } else {
          unconfirmedCount++;
        }

        // 只有展開的檔案才統計分類
        if (shouldIncludeFileDetails) {
          const category = feature.properties?.category || 'Unknown';
          categoryCount[category] = (categoryCount[category] || 0) + 1;
        }
      });
    });

    // 轉換為圖表數據格式並排序
    const categoryStats: FeatureStats[] = Object.entries(categoryCount)
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count);

    const fileStats = Object.entries(fileCount)
      .map(([fileName, count]) => ({ 
        fileName: fileName.replace('.geojson', ''), 
        count 
      }))
      .sort((a, b) => b.count - a.count);

    // 圖層類型統計（按名稱排序）
    const layerTypeNames: Record<string, string> = {
      venue: '場館',
      building: '建築',
      level: '樓層',
      unit: '單元',
      amenity: '設施',
      opening: '開口',
      fixture: '裝置',
      anchor: '錨點',
      other: '其他'
    };

    const layerStats = Object.entries(layerCount)
      .map(([layerType, count]) => ({ 
        layerType, 
        layerName: layerTypeNames[layerType] || layerType,
        count 
      }))
      .sort((a, b) => b.count - a.count);

    return {
      categoryStats,
      fileStats,
      layerStats,
      confirmStats: { confirmed: confirmedCount, unconfirmed: unconfirmedCount },
    };
  }, [imdfData, expandedFiles]);

  if (!imdfData || imdfData.files.length === 0) {
    return (
      <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">資訊統計</h3>
        <div className="text-center py-8 text-gray-500">
          沒有可統計的資料
        </div>
      </div>
    );
  }

  const confirmPieData = [
    { name: '已確認', value: stats.confirmStats.confirmed, color: '#10B981' },
    { name: '未確認', value: stats.confirmStats.unconfirmed, color: '#EF4444' },
  ];

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">資訊統計</h3>
        <div className="mt-1 flex items-center space-x-6 text-sm text-gray-500">
          <span>總 Features: {imdfData.totalFeatures}</span>
          <span>檔案數: {imdfData.files.length}</span>
          <span>圖層類型: {stats.layerStats.length}</span>
          <span>已確認: {stats.confirmStats.confirmed}</span>
          <span>未確認: {stats.confirmStats.unconfirmed}</span>
        </div>
      </div>

      <div className="p-6 space-y-8">
        {/* Category 統計長條圖 */}
        <div>
          <h4 className="text-base font-medium text-gray-900 mb-4">分類統計 (Category)</h4>
          {stats.categoryStats.length > 0 ? (
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={stats.categoryStats} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="category" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-8 text-center">
              <div className="text-gray-400 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <p className="text-sm text-gray-500">請展開上方檔案列表以檢視分類統計</p>
            </div>
          )}
        </div>

        {/* IMDF 圖層類型統計長條圖 */}
        {stats.layerStats.length > 0 && (
          <div>
            <h4 className="text-base font-medium text-gray-900 mb-4">IMDF 圖層類型統計</h4>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={stats.layerStats} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="layerName" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                  />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8B5CF6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 檔案統計長條圖 */}
          {stats.fileStats.length > 0 && (
            <div>
              <h4 className="text-base font-medium text-gray-900 mb-4">檔案統計</h4>
              <div style={{ width: '100%', height: 250 }}>
                <ResponsiveContainer>
                  <BarChart data={stats.fileStats} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="fileName"
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#10B981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* 確認狀態圓餅圖 */}
          {(stats.confirmStats.confirmed > 0 || stats.confirmStats.unconfirmed > 0) && (
            <div>
              <h4 className="text-base font-medium text-gray-900 mb-4">確認狀態分布</h4>
              <div style={{ width: '100%', height: 250 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={confirmPieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value, percent }) => `${name}: ${value} (${((percent || 0) * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {confirmPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* 統計表格 */}
        {stats.categoryStats.length > 0 && (
          <div>
            <h4 className="text-base font-medium text-gray-900 mb-4">詳細統計</h4>
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      分類 (Category)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      數量
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      百分比
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stats.categoryStats.map((item, index) => (
                    <tr key={item.category} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.category}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {((item.count / imdfData.totalFeatures) * 100).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* IMDF 圖層類型統計表格 */}
        {stats.layerStats.length > 0 && (
          <div>
            <h4 className="text-base font-medium text-gray-900 mb-4">IMDF 圖層類型詳細統計</h4>
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      圖層類型
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      數量
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      百分比
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stats.layerStats.map((item, index) => (
                    <tr key={item.layerType} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.layerName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {((item.count / imdfData.totalFeatures) * 100).toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IMDFStatsChart;